/**
 * Escrita das declaracoes do pacote no proprio `SKILL.md` (AB#85634).
 *
 * === Por que escrever no arquivo, e nao guardar ao lado ===
 *
 * As integracoes exigidas e os parametros da instalacao sao lidos do
 * frontmatter da versao publicada. Se o formulario de publicacao guardasse essa
 * declaracao numa coluna do Prodgy, o `SKILL.md` armazenado passaria a dizer
 * uma coisa e a instalacao a exigir outra — e quem baixasse o pacote de volta,
 * ou o levasse para outro host, perderia tudo o que foi declarado aqui.
 * Escrevendo no arquivo, o pacote continua sendo a verdade inteira.
 *
 * === O que este modulo NAO faz ===
 *
 * Nao reescreve o frontmatter inteiro. Ele remove os blocos `integrations` e
 * `parameters`, se existirem, e escreve os novos no lugar — o resto do arquivo,
 * comentarios e ordem das chaves inclusive, chega intacto do outro lado. Um
 * emissor de YAML completo daria conta, mas trocaria a autoria do arquivo por
 * uma normalizacao que ninguem pediu.
 */
const DELIMITER = '---';
/**
 * Devolve o `SKILL.md` com os blocos `integrations` e `parameters` atualizados.
 *
 * Lista vazia REMOVE o bloco: e assim que quem publica desfaz uma exigencia, e
 * a ausencia do bloco e o que faz a etapa correspondente sumir da instalacao.
 *
 * Arquivo sem frontmatter volta como veio — a recusa por falta dele e da
 * leitura, que acontece antes, e nao cabe a este modulo inventar um.
 */
export function writeDeclarations(source, declarations) {
    const texto = source.replace(/^﻿/, '').replace(/\r\n/g, '\n');
    const linhas = texto.split('\n');
    if (linhas[0]?.trim() !== DELIMITER)
        return source;
    const fechamento = linhas.findIndex((linha, indice) => indice > 0 && linha.trim() === DELIMITER);
    if (fechamento === -1)
        return source;
    const cabecalho = comEscalares(semBlocos(linhas.slice(1, fechamento), ['integrations', 'parameters']), [
        ['title', declarations.title],
        ['description', declarations.description],
    ]);
    const corpo = linhas.slice(fechamento + 1);
    const novos = [
        ...emiteIntegracoes(declarations.integrations),
        ...emiteParametros(declarations.parameters),
    ];
    return [DELIMITER, ...cabecalho, ...novos, DELIMITER, ...corpo].join('\n');
}
/**
 * Troca o valor de chaves escalares do topo, ou as acrescenta quando faltam.
 *
 * Valor `undefined` nao mexe na chave; `null` ou vazio a REMOVE, que e como
 * quem publica desfaz um titulo que o pacote nao deveria declarar.
 */
function comEscalares(cabecalho, pares) {
    let linhas = cabecalho;
    for (const [chaveEscalar, valor] of pares) {
        if (valor === undefined)
            continue;
        const limpo = (valor ?? '').trim();
        const indice = linhas.findIndex((linha) => {
            const par = /^([A-Za-z0-9_-]+)\s*:/.exec(linha);
            return par?.[1] === chaveEscalar;
        });
        if (!limpo) {
            linhas = indice === -1 ? linhas : linhas.filter((_, i) => i !== indice);
            continue;
        }
        const nova = `${chaveEscalar}: ${escalar(limpo)}`;
        if (indice === -1)
            linhas = [...linhas, nova];
        else
            linhas = linhas.map((linha, i) => (i === indice ? nova : linha));
    }
    return linhas;
}
/**
 * Tira do frontmatter os blocos de topo pedidos, com tudo o que esta dentro.
 *
 * O que esta "dentro" e o que vem indentado ate a proxima chave na coluna zero;
 * linha em branco no meio do bloco continua sendo do bloco, senao um espaco
 * gratuito partiria a declaracao ao meio.
 */
function semBlocos(cabecalho, chaves) {
    const restantes = [];
    let pulando = false;
    for (const linha of cabecalho) {
        const indentada = /^\s/.test(linha) || linha.trim() === '';
        if (pulando && indentada)
            continue;
        pulando = false;
        const chave = /^([A-Za-z0-9_-]+)\s*:/.exec(linha);
        if (chave && chaves.includes(chave[1])) {
            pulando = true;
            continue;
        }
        restantes.push(linha);
    }
    // Linhas em branco que sobraram no fim, de um bloco removido no final do
    // frontmatter, nao tem por que ficar.
    while (restantes.length > 0 && restantes[restantes.length - 1].trim() === '')
        restantes.pop();
    return restantes;
}
function emiteIntegracoes(integracoes) {
    const validas = integracoes.filter((integracao) => chave(integracao.id) && integracao.anyOf.some((forma) => forma.trim()));
    if (validas.length === 0)
        return [];
    const linhas = ['integrations:'];
    for (const integracao of validas) {
        linhas.push(`  ${chave(integracao.id)}:`);
        linhas.push('    any-of:');
        for (const forma of integracao.anyOf.map((item) => item.trim().toLowerCase()).filter(Boolean)) {
            linhas.push(`      - ${escalar(forma)}`);
        }
        const razao = (integracao.reason ?? '').trim();
        if (razao)
            linhas.push(`    reason: ${escalar(razao)}`);
    }
    return linhas;
}
function emiteParametros(parametros) {
    const validos = parametros.filter((parametro) => chave(parametro.key));
    if (validos.length === 0)
        return [];
    const linhas = ['parameters:'];
    for (const parametro of validos) {
        const opcoes = (parametro.options ?? []).map((opcao) => opcao.trim()).filter(Boolean);
        linhas.push(`  ${chave(parametro.key)}:`);
        // `enum` e o tipo do Prodgy; no arquivo ele e um `string` COM `enum`, que e
        // como o vocabulario de JSON Schema expressa lista fechada.
        linhas.push(`    type: ${escalar(tipoNoArquivo(parametro.type))}`);
        if (opcoes.length > 0) {
            linhas.push('    enum:');
            for (const opcao of opcoes)
                linhas.push(`      - ${escalar(opcao)}`);
        }
        const rotulo = (parametro.label ?? '').trim();
        if (rotulo)
            linhas.push(`    title: ${escalar(rotulo)}`);
        const ajuda = (parametro.help ?? '').trim();
        if (ajuda)
            linhas.push(`    description: ${escalar(ajuda)}`);
        const padrao = (parametro.default ?? '').trim();
        if (padrao)
            linhas.push(`    default: ${escalar(padrao)}`);
        if (parametro.required)
            linhas.push('    required: true');
    }
    return linhas;
}
function tipoNoArquivo(tipo) {
    if (tipo === 'number')
        return 'number';
    if (tipo === 'boolean')
        return 'boolean';
    return 'string';
}
/** Chave no formato que o leitor aceita; o que nao casa vira vazio e e descartado. */
function chave(bruta) {
    return bruta.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/^-+|-+$/g, '');
}
/**
 * Um escalar entre aspas, em UMA linha.
 *
 * O leitor tira as aspas sem desescapar nada, entao aspas duplas no meio do
 * texto o quebrariam: viram simples. Quebra de linha vira espaco pelo mesmo
 * motivo — este formato nao tem texto multilinha, e emitir um produziria um
 * arquivo que ele mesmo nao consegue ler de volta.
 */
function escalar(valor) {
    const limpo = valor.replace(/[\r\n]+/g, ' ').replace(/"/g, "'").trim();
    return `"${limpo}"`;
}
//# sourceMappingURL=declarations.js.map